import { ethers, BrowserProvider, formatEther } from 'ethers';
import { Hash } from '../features/loans/loanAPI';
import { useDispatch } from 'react-redux';
import { setAccount, setData, resetEthersState } from '../features/ethers/ethersSlice';

const useEthers = () => {
    const dispatch = useDispatch();

    const networks = [
        {
            id: "polygon",
            name: "polygon",
            chainId: "0x89", // Hexadecimal of 137
            rpcUrls: ["https://polygon-rpc.com"],
            nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
            blockExplorerUrls: ["https://polygonscan.com"]
        },
        {
            id: "base",
            name: "base",
            chainId: "0x2105", // Hexadecimal of 8453
            rpcUrls: ["https://mainnet.base.org"],
            nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
            blockExplorerUrls: ["https://basescan.org"]
        },
        {
            id: "arbitrum",
            name: "arbitrum",
            chainId: "0xa4b1", // Hexadecimal of 42161
            rpcUrls: ["https://arb1.arbitrum.io/rpc"],
            nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
            blockExplorerUrls: ["https://arbiscan.io"]
        },
        {
            id: "optimism",
            name: "optimism",
            chainId: "0xa", // Hexadecimal of 10
            rpcUrls: ["https://mainnet.optimism.io"],
            nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
            blockExplorerUrls: ["https://optimistic.etherscan.io"]
        },
        {
            id: "solana",
            name: "solana",
            chainId: null, // Solana doesn't use chainId like EVM chains
            rpcUrls: ["https://api.mainnet-beta.solana.com"],
            nativeCurrency: { name: "SOL", symbol: "SOL", decimals: 9 },
            blockExplorerUrls: ["https://solscan.io"]
        }
    ];

    const erc20Abi = [
        "function balanceOf(address owner) view returns (uint256)",
        "function transfer(address to, uint amount) returns (bool)",
        "function approve(address spender, uint256 amount) returns (bool)",
        "function allowance(address owner, address spender) view returns (uint256)",
        "function decimals() view returns (uint8)"
    ];

    const tokenAddresses = {
        "polygon": {
            "USDC": "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
            "USDT": "0xc2132D05D31c914a87C6611C10748AEb04B58e8F"
        },
        "arbitrum": {
            "USDC": "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
            "USDT": "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9"
        },
        "optimism": {
            "USDC": "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85",
            "USDT": "0x94b008aa00579c1307b0ef2c499ad98a8ce58e58"
        },
        "base": {
            "USDC": "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913"
        },
        "sepolia": {
            "Link": "0x779877A7B0D9E8603169DdbD7836e478b4624789"
        },
        "solana": {
            "USDC": "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
            "USDT": "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"
        }
    };

    const connectMetaMask = async () => {
        if (window.ethereum !== 'undefined') {
            try {
                await window.ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: "0xaa36a7" }]
                });
            } catch (err) {
                if (err.code === 4902) {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: "0xaa36a7",
                            rpcUrls: ["https://arbitrum-sepolia.blockpi.network/v1/rpc/public"],
                            chainName: "Arbitrum Sepolia",
                            nativeCurrency: {
                                name: "ETH",
                                symbol: "ETH",
                                decimals: 18
                            },
                            blockExplorerUrls: ["https://sepolia.arbiscan.io"]
                        }]
                    });
                }
            }
            const accounts = await window.ethereum.request({
                method: 'eth_requestAccounts'
            });
            const provider = new BrowserProvider.Web3Provider(window.ethereum);
            const tokenContract = new ethers.Contract(tokenAddresses["sepolia"]["Link"], erc20Abi, provider);
            dispatch(setAccount(accounts[0]));
            try {
                const balance = await tokenContract.balanceOf(accounts[0]);
                if (!balance) {
                    console.log("Balance fetch failed");
                    return;
                }
                const formattedBalance = formatEther.formatUnits(balance, 18);
                dispatch(setData({
                    address: accounts[0],
                    balance: formattedBalance
                }));
            } catch (error) {
                console.error("Error fetching balance:", error.message);
            }
        } else {
            console.log("Install MetaMask extension!!");
        }
    };

    const Transfer = async (e, wallet, amount, id, block, coin) => {
        e.preventDefault();
        const tokenAddress = tokenAddresses[block][coin];
        try {
            const provider = new BrowserProvider.Web3Provider(window.ethereum);
            const signer = provider.getSigner();
            const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, signer);
            const tx = await tokenContract.transfer(wallet, formatEther.parseUnits(amount, 18));
            await dispatch(Hash({
                _id: id,
                hash: tx.hash
            }))
                .unwrap()
                .then(() => {
                    console.log('Hash added successfully');
                })
                .catch((error) => {
                    console.error('Error adding Hash:', error.message || error);
                });
            console.log('Transaction hash:', tx.hash);
            await tx.wait();
            console.log('Transaction confirmed');
            return true;
        } catch (err) {
            console.log('Error:', err.message);
            return false;
        }
    };

    async function switchNetwork(block) {
        const network = networks.find(n => n.id === block);
        const chain = network.chainId;
        try {
            await window.ethereum.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: chain }]
            });
            console.log(`Switched to network with chainId ${network.chainId}`);
        } catch (error) {
            if (error.code === 4902) {
                console.log("Network not found in MetaMask. Attempting to add it.");
                if (network) {
                    try {
                        await window.ethereum.request({
                            method: "wallet_addEthereumChain",
                            params: [{
                                chainId: network.chainId,
                                chainName: network.name,
                                rpcUrls: network.rpcUrls,
                                nativeCurrency: network.nativeCurrency,
                                blockExplorerUrls: network.blockExplorerUrls
                            }],
                        });
                    } catch (addError) {
                        console.error("Failed to add the network:", addError);
                    }
                } else {
                    console.error("Network not defined in the configuration.");
                }
            } else {
                console.error("Failed to switch network:", error);
            }
        }
    };

    const disconnectMetaMask = () => {
        dispatch(resetEthersState());
    };

    return { connectMetaMask, switchNetwork, Transfer, disconnectMetaMask };
};

export default useEthers;
